import { Product } from '@shared/models/product.model';
import { Subscription } from '@shared/models/subscription-model';
import { Subscriber } from '@shared/models/subscriber.model';
import { PhoneNumber } from '@shared/models/phone-number.model';
import { VoiceProduct } from '@shared/models/voice-product.model';
import {
  VoiceSubscriptionOffering,
  VoiceAllowedChangeInfo,
  VoiceChangeInfo,
} from '@sales/modules/subscription/models/offerings-response.model';
import {
  AdditionalServiceVoice,
  AdditionalServiceSet,
  WhitelistEntries,
} from '@sales/modules/subscription/models/additional-service-voice.model';
import { AssignedSubscriptionModel } from '@shared/models/assigned-subscription-offering.model';
import { Price } from '@shared/models/price.model';
import { PortingWindowsResponse } from '../../modules/subscription/services/porting.service';
import { Sponsor, SponsorType } from '@shared/models/sponsor.model';
import { SimCardType, SimCardVariant } from '@sales/models/simcard.model';

export class ProductAddAction {
  public static readonly type = '[Product] Add voice product';
  constructor(
    public productToClone?: VoiceProduct,
    public mobileProductId?: string,
    public accessoryProductId?: string
  ) {}
}

export class VoiceProductUpdateAction {
  public static readonly type = '[Product] Update voice product offering';
  constructor(
    public productId: string,
    public offering: VoiceSubscriptionOffering,
    public changeInfo: VoiceChangeInfo
  ) {}
}

export class VoiceProductUpdateSponsorAction {
  public static readonly type = '[Product] Update voice product sponsor';
  constructor(
    public productId: string,
    public sponsor: Sponsor
  ) {}
}

export class VoiceProductUpdateSponsorTypeAction {
  public static readonly type = '[Product] Update voice product sponsor type';
  constructor(
    public productId: string,
    public sponsorType: SponsorType
  ) {}
}

export class ProductSubscriberUpdateAction {
  public static readonly type = '[Product] Update product subscriber';
  constructor(
    public productId: string,
    public subscriber: Subscriber
  ) {}
}

export class ProductPhoneNumberUpdateAction {
  public static readonly type = '[Product] Update product phoneNumber';
  constructor(
    public productId: string,
    public phoneNumber: PhoneNumber
  ) {}
}

export class ProductRemoveConfirmAction {
  public static readonly type = '[Product] Confirm remove voice product';
  constructor(public productId: string) {}
}

export class RemoveExistingProductAction {
  public static readonly type = '[Product] remove existing voice product';
  constructor(public productId: string) {}
}

export class ResetOfferingOnExistingSubscriptionAction {
  public static readonly type = '[Product] Reset offering on existing subscription';
  constructor(public productId: string) {}
}

export class AdditionalServiceAddAction {
  public static readonly type = '[Product] Add additional service';
  constructor(
    public id: string,
    public additionalService: AdditionalServiceVoice,
    public isMutex: boolean = false
  ) {}
}

export class AddAdditionalServiceToExistingSubscriptionAction {
  public static readonly type = '[Product] Add additional service to existing voice subscription';
  constructor(
    public id: string,
    public additionalService: AdditionalServiceVoice,
    public isMutex: boolean = false
  ) {}
}

export class RemoveAdditionalServiceFromExistingSubscriptionAction {
  public static readonly type = '[Product] Remove additional service from existing voice subscription';
  constructor(
    public id: string,
    public additionalService: AdditionalServiceVoice,
    public isMutex: boolean = false
  ) {}
}

export class AdditionalServiceUpdateICCAction {
  public static readonly type = '[Product] Update ICC on additional service';
  constructor(
    public product: Product,
    public additionalService: AdditionalServiceVoice,
    public icc: string
  ) {}
}

export class AdditionalServiceUpdateOrderSimAction {
  public static readonly type = '[Product] Update OrderSim on additional service';
  constructor(
    public product: VoiceProduct,
    public additionalService: AdditionalServiceVoice,
    public orderSim: boolean
  ) {}
}

export class UpdateAdditionalServiceToExistingSubscriptionAction {
  public static readonly type = '[Product] Update additional service to existing voice subscription';
  constructor(
    public id: string,
    public additionalService: AdditionalServiceVoice,
    public icc: string,
    public orderSim: boolean
  ) {}
}

export class AdditionalServiceRemoveAction {
  public static readonly type = '[Product] Remove additional service';
  constructor(
    public productId: string,
    public additionalService: AdditionalServiceVoice
  ) {}
}

export class ProductResetAction {
  static readonly type = '[Product] Reset customer product state';
}

export class ApiGetAssignedSubscriptionsAction {
  public static readonly type = '[Api] Get assigned subscriptions';
  constructor(public subscriptionIds: string[]) {}
}

export class ApiGetAssignedSubscriptionsSuccessAction {
  static readonly type = '[Api] Get assigned subscriptions success';
  constructor(public assignedSubscriptions: AssignedSubscriptionModel[]) {}
}

export class ApiGetAssignedSubscriptionsErrorAction {
  static readonly type = '[Api] Get assigned subscriptions Error';
}

export class ApiGetSubscriptionCostsAction {
  static readonly type = '[Api Subscription Costs] Get subscription costs';
  constructor(public subscriptionId: number) {}
}

export class ApiGetSubscriptionCostsSuccessAction {
  static readonly type = '[Api Subscription Costs] Get subscription costs success';
  constructor(
    public subscriptionId: number,
    public newPrice: Price
  ) {}
}

export class ApiGetSubscriptionCostsErrorAction {
  static readonly type = '[Api Subscription Costs] Get subscription costs error';
}

export class ApiGetAllSubscriptionOfferingsAction {
  public static readonly type = '[API] Get all subscription offerings';
}

export class ApiGetSubscriptionOfferingsAction {
  public static readonly type = '[API] Get subscription offerings';
  constructor(public subscriptionId: string) {}
}

export class SetAssignedSubscriptionAction {
  static readonly type = '[Product] Set assigned subscriptions';
  constructor(public assignedSubscription: AssignedSubscriptionModel) {}
}

export class ApiGetSubscriptionOfferingsSuccessAction {
  public static readonly type = '[API] Get subscription offerings success';
  constructor(
    public subscriptionId: string,
    public offerings: VoiceAllowedChangeInfo[]
  ) {}
}

export class ApiGetSubscriptionOfferingsErrorAction {
  public static readonly type = '[API] Get subscription offerings error';
  constructor(
    public subscriptionId: string,
    public error: string
  ) {}
}

export class ApiGetSubscriptionServicesAction {
  static readonly type = '[Api] Get subscription services';
  constructor(public subscriptionId: number) {}
}

export class ApiGetSubscriptionServicesSuccessAction {
  static readonly type = '[Api] Get subscription services success';
  constructor(
    public subscriptionId: number,
    public subscriptionServices: AdditionalServiceSet
  ) {}
}

export class ApiGetSubscriptionServicesErrorAction {
  static readonly type = '[Api] Get subscription services error';
  constructor(public subscriptionId: number) {}
}

export class SetExistingVoiceSubscriptionsAndAddNewSubscriptionsAction {
  static readonly type = '[Product] Set existing voice subscriptions and add new subscriptions';
  constructor(public voiceSubscriptions: Subscription[]) {}
}

export class ResetAdditionalServicesChangesOnExistingSubscriptionAction {
  static readonly type = '[Product] Reset additionalservices Changes on existing Subscription';
  constructor(public productId: string) {}
}

export class ApiGetSubscriberForVoiceSubscriptionAction {
  static readonly type = '[Api] Get subscriber for existing Voice Subscription';
  constructor(public subscriptionId: string) {}
}

export class ApiGetSubscriberForVoiceSubscriptionSuccessAction {
  static readonly type = '[Api] Get subscriber for existing Voice Subscription success';
  constructor(
    public subscriptionId: string,
    public subscriber: Subscriber
  ) {}
}

export class ApiGetSubscriberForVoiceSubscriptionErrorAction {
  static readonly type = '[Api] Get subscriber for existing Voice Subscription error';
  constructor(public subscriptionId: string) {}
}

export class SetMobileProductOnVoiceProductAction {
  public static readonly type = '[Product] Set mobile product on voice product';
  constructor(
    public productId: string,
    public mobileProductId: string
  ) {}
}

export class ToggleSelectedSubscriptionAction {
  public static readonly type = '[Voice Subscription] Toggle selected subscription';
  constructor(public id: string) {}
}

export class SetSelectedSubscriptionIdAction {
  public static readonly type = '[Voice Subscription] Set selected subscription id';
  constructor(public id: string) {}
}

export class SetSelectedStepIndexAction {
  public static readonly type = '[Voice Subscription] Set selected step index';
  constructor(public index: number) {}
}

export class ResetSelectedSubscriptionAction {
  public static readonly type = '[Voice Subscription] Reset selected subscription';
}

export class LoadingPortingDatesAction {
  static readonly type = '[API Porting] Loading porting dates';
}

export class ApiGetPortingDatesAction {
  static readonly type = '[API Porting] Get porting dates';
}

export class ApiGetPortingDatesSuccessAction {
  static readonly type = '[API Porting] Get porting dates success';
  constructor(public portingDates: PortingWindowsResponse) {}
}

export class ApiGetPortingDatesErrorAction {
  static readonly type = '[API Porting] Get porting dates error';
}

export class ReplaceSimCardAction {
  public static readonly type = '[Voice Product] Replace simcard';
  constructor(
    public productId: string,
    public newIcc: string,
    public simCardType: SimCardType,
    public simCardVariant: SimCardVariant,
    public additionalSimANumber?: string
  ) {}
}

export class CombineExistingSubscriptionServicesWithOfferingServices {
  public static readonly type = '[Voice product] Combine existing subscription services with offering services';
  constructor(
    public productId: string,
    public additionalServiceOfferings: AdditionalServiceSet
  ) {}
}

export class UpdateCustomerServiceProductsAction {
  public static readonly type = '[Voice product] Update customer service products';
}

export class UpdateWhitelistEntriesAction {
  public static readonly type = '[Voice product] Update whitelist entry';
  constructor(
    public productId: string,
    public whitelistEntries: WhitelistEntries
  ) {}
}

export class SetAccessoryProductIdOnVoiceProductAction {
  public static readonly type = '[Voice product] Set accessory product id on voice product';
  constructor(
    public productId: string,
    public accessoryProductId: string
  ) {}
}

export class DelayESimPorting {
  public static readonly type = '[Voice product] Delay porting time on ESim voice products';
  constructor(public newPortingDate: Date) {}
}

export class CustomerProductsMoreThanAllowedAction {
  public static readonly type = '[Voice product] Customer products more than allowed';
  constructor() {}
}
